import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Megaphone, Edit, Trash2, Eye, EyeOff, X, Link as LinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const AdsTab = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    image_url: "",
    link_url: "",
    placement: "homepage",
    is_active: true,
    priority: 0
  });

  const placements = [
    { id: "all_pages", label: "📢 All Pages (Site-wide)" },
    { id: "homepage", label: "Homepage" },
    { id: "homepage_top", label: "Homepage - Top Banner" },
    { id: "homepage_middle", label: "Homepage - Middle Banner" },
    { id: "homepage_bottom", label: "Homepage - Bottom Banner" },
    { id: "sidebar", label: "Sidebar (General)" },
    { id: "article_side", label: "📰 Article Sidebar (300x250)" },
    { id: "header", label: "Header" },
    { id: "footer", label: "Footer" },
    { id: "schedule", label: "Schedule Page" },
    { id: "watch", label: "Watch Page" },
    { id: "on-demand", label: "On Demand Page" },
    { id: "news", label: "News Page" },
    { id: "photos", label: "Photos Page" },
    { id: "events", label: "Events Page" },
    { id: "game", label: "Game Player Page" }
  ];

  const fetchAds = async () => {
    try {
      const [adsRes, statsRes] = await Promise.all([
        axios.get(`${API}/ads`, { headers: getAuthHeader() }),
        axios.get(`${API}/ads/stats`, { headers: getAuthHeader() })
      ]);
      setAds(adsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAds(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAd) {
        await axios.put(`${API}/ads/${editingAd.id}`, formData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/ads`, formData, { headers: getAuthHeader() });
      }
      setShowForm(false);
      setEditingAd(null);
      setFormData({ name: "", image_url: "", link_url: "", placement: "homepage", is_active: true, priority: 0 });
      fetchAds();
    } catch (error) {
      console.error("Error saving ad:", error);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      name: ad.name,
      image_url: ad.image_url,
      link_url: ad.link_url || "",
      placement: ad.placement,
      is_active: ad.is_active,
      priority: ad.priority
    });
    setShowForm(true);
  };

  const handleDelete = async (adId) => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;
    try {
      await axios.delete(`${API}/ads/${adId}`, { headers: getAuthHeader() });
      fetchAds();
    } catch (error) {
      console.error("Error deleting ad:", error);
    }
  };

  const handleToggle = async (adId) => {
    try {
      await axios.post(`${API}/ads/${adId}/toggle`, {}, { headers: getAuthHeader() });
      fetchAds();
    } catch (error) {
      console.error("Error toggling ad:", error);
    }
  };

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Ad Banners</h2>
        <Button 
          onClick={() => { setShowForm(true); setEditingAd(null); setFormData({ name: "", image_url: "", link_url: "", placement: "homepage", is_active: true, priority: 0 }); }}
          className="bg-white text-black text-xs px-4 py-2 h-auto rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Banner
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-white">{stats.total_ads}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Total Ads</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-green-500">{stats.active_ads}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Active</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-blue-500">{stats.total_impressions?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Impressions</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-yellow-500">{stats.total_clicks?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Clicks</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-white">{stats.ctr || 0}%</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">CTR</p>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-secondary text-lg font-bold uppercase text-white">{editingAd ? "Edit Ad" : "New Ad Banner"}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Banner Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" placeholder="e.g., Homepage Hero Banner" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Image URL *</label>
                <input type="url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} required className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" placeholder="https://..." />
                {formData.image_url && (
                  <img src={formData.image_url} alt="Preview" className="mt-2 max-h-32 object-contain border border-white/10" />
                )}
              </div>
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Link URL (Optional)</label>
                <input type="url" value={formData.link_url} onChange={(e) => setFormData({...formData, link_url: e.target.value})} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" placeholder="https://... (where users go when clicked)" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-2">Placement *</label>
                  <select value={formData.placement} onChange={(e) => setFormData({...formData, placement: e.target.value})} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm">
                    {placements.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-2">Priority</label>
                  <input type="number" value={formData.priority} onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" min="0" max="100" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4" />
                <label htmlFor="is_active" className="text-sm text-zinc-400">Active (show on site)</label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-zinc-800 text-white text-xs px-4 py-2 h-auto rounded-none">Cancel</Button>
                <Button type="submit" className="flex-1 bg-white text-black text-xs px-4 py-2 h-auto rounded-none">{editingAd ? "Update" : "Create"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ads List */}
      <div className="space-y-4">
        <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white">All Ad Banners</h3>
        
        {ads.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Megaphone className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No ad banners yet</p>
            <p className="text-xs text-zinc-600 mt-2">Create banners to display throughout the site</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ads.map(ad => (
              <div key={ad.id} className="bg-zinc-900/50 border border-white/5 overflow-hidden">
                {/* Preview */}
                <div className="relative aspect-[3/1] bg-zinc-800">
                  <img src={ad.image_url} alt={ad.name} className="w-full h-full object-cover" />
                  {!ad.is_active && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Inactive</span>
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-secondary text-sm font-bold uppercase text-white">{ad.name}</h4>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${ad.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                      {ad.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
                    <span className="bg-zinc-800 px-2 py-1">{placements.find(p => p.id === ad.placement)?.label || ad.placement}</span>
                    <span>Priority: {ad.priority}</span>
                  </div>
                  
                  {ad.link_url && (
                    <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mb-3">
                      <LinkIcon className="w-3 h-3" /> {ad.link_url.substring(0, 40)}...
                    </a>
                  )}
                  
                  <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
                    <span>{ad.impression_count?.toLocaleString() || 0} impressions</span>
                    <span>{ad.click_count?.toLocaleString() || 0} clicks</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                    <button onClick={() => handleToggle(ad.id)} className={`p-2 transition-colors ${ad.is_active ? 'text-green-500 hover:text-green-400' : 'text-zinc-500 hover:text-green-400'}`} title={ad.is_active ? "Deactivate" : "Activate"}>
                      {ad.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleEdit(ad)} className="p-2 text-zinc-500 hover:text-white transition-colors" title="Edit">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(ad.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdsTab;
